import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NbButtonModule, NbCardModule, NbDialogRef, NbIconModule, NbSelectModule, NbSpinnerModule, NbTooltipModule } from '@nebular/theme';
import { XeroTenant } from 'src/app/models';
import { Conversion } from 'src/app/models/conversion.model';
import { XeroAuthService } from 'src/app/shared/services/xero-auth.service';
import {
  canSendToXero,
  XeroAccountsResult,
  XeroBankAccount,
  XeroBankFeedService,
  XeroOrgContext,
  XeroPushPreflight,
  XeroPushState,
  xeroReconcileUrl,
} from 'src/app/shared/services/xero-bank-feed.service';

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 10;

/** Sentinel dropdown value: opens Xero's add-bank-account screen, selects nothing */
const ADD_ACCOUNT_IN_XERO = '__addAccountInXero__';

/**
 * The dialog's flow, as a discriminated union — exactly one phase is active
 * and each phase carries only the data it needs.
 *
 *   initializing → connectRequired
 *                → blocked (ineligible statement, explained up front)
 *                → ready (pick destination; preflight may still be resolving)
 *                → sending → result (delivered / pending / rejected)
 */
type Phase =
  | { kind: 'initializing' }
  | { kind: 'connectRequired' }
  | { kind: 'blocked'; title: string; detail: string }
  | { kind: 'ready' }
  | { kind: 'sending' }
  | { kind: 'result'; push: XeroPushState };

@Component({
  selector: 'app-send-to-xero-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, NbCardModule, NbButtonModule, NbSelectModule, NbSpinnerModule, NbIconModule, NbTooltipModule],
  templateUrl: './send-to-xero-dialog.component.html',
  styleUrls: ['./send-to-xero-dialog.component.scss'],
})
export class SendToXeroDialogComponent implements OnInit, OnDestroy {
  @Input() conversion: Conversion;

  phase: Phase = { kind: 'initializing' };

  preflight: XeroPushPreflight | null = null;

  tenants: XeroTenant[] = [];
  selectedTenantId: string | null = null;

  accounts: XeroBankAccount[] = [];
  organisation: XeroOrgContext | null = null;
  accountsLoading = false;
  accountsFailed = false;
  selectedAccountId: string | null = null;
  ADD_ACCOUNT_IN_XERO = ADD_ACCOUNT_IN_XERO;

  /** Last push state — returned to the opener so the row updates in place */
  private lastPush: XeroPushState | null = null;

  /** Set once the dialog is gone so an in-flight delivery poll stops */
  private destroyed = false;

  constructor(
    protected dialogRef: NbDialogRef<SendToXeroDialogComponent>,
    private xeroAuth: XeroAuthService,
    private bankFeed: XeroBankFeedService,
  ) {}

  async ngOnInit() {
    try {
      // Preflight, connection state and accounts all race — the destination
      // picker becomes interactive immediately; preflight gates only Send.
      const preflightPromise = this.bankFeed.preflight(this.conversion.uid);
      const provider = await this.bankFeed.getProvider();

      if (!canSendToXero(provider)) {
        this.phase = { kind: 'connectRequired' };
        return;
      }

      this.tenants = provider.tenants;
      this.selectedTenantId = this.tenants[0].tenantId;
      this.phase = { kind: 'ready' };
      this.loadAccounts(); // deliberately not awaited

      const preflight = await preflightPromise;
      this.preflight = preflight;
      if (this.phase.kind === 'ready' && (!preflight || !preflight.eligible)) {
        this.phase = {
          kind: 'blocked',
          title: "This statement can't be sent",
          detail: preflight?.reason || 'Something went wrong while checking the statement. Please try again.',
        };
      }
    } catch (error) {
      console.error('Failed to prepare Send to Xero dialog:', error);
      this.phase = {
        kind: 'blocked',
        title: "This statement can't be sent",
        detail: 'Something went wrong while checking the statement. Please try again.',
      };
    }
  }

  // Destination

  async onTenantChange() {
    // Drop the previous org's accounts immediately so none can be picked
    // while the new org's list loads.
    this.selectedAccountId = null;
    this.accounts = [];
    this.organisation = null;
    await this.loadAccounts();
  }

  /** The "Add a bank account" option redirects to Xero rather than selecting */
  onAccountSelect(value: string) {
    if (value !== ADD_ACCOUNT_IN_XERO) return;
    this.selectedAccountId = null;
    if (this.organisation?.addBankAccountUrl) window.open(this.organisation.addBankAccountUrl, '_blank');
  }

  async loadAccounts(force = false) {
    if (!this.selectedTenantId) return;
    const tenantId = this.selectedTenantId;
    this.accountsLoading = !this.accounts.length || force;
    this.accountsFailed = false;

    try {
      const result = force
        ? await this.bankFeed.refreshBankAccounts(tenantId)
        : await this.bankFeed.getBankAccounts(tenantId, (fresh) => this.applyAccounts(tenantId, fresh));
      if (result) this.applyAccounts(tenantId, result);
      // null = the fetch failed (already toasted); an org with no bank accounts
      // yet comes back as a real result with an empty list
      this.accountsFailed = !result;
    } catch {
      this.accountsFailed = true;
    } finally {
      this.accountsLoading = false;
    }
  }

  /** Ignore late responses if the user has switched org in the meantime */
  private applyAccounts(tenantId: string, result: XeroAccountsResult) {
    if (tenantId !== this.selectedTenantId) return;
    this.accounts = result.accounts;
    this.organisation = result.organisation;
    if (this.selectedAccountId && !this.accounts.some((a) => a.accountId === this.selectedAccountId)) {
      this.selectedAccountId = null;
    }
  }

  // Send

  async send() {
    const account = this.selectedAccount;
    if (!this.canSend || !account || !this.selectedTenantId) return;
    this.phase = { kind: 'sending' };

    const response = await this.bankFeed.pushConversion(this.conversion.uid, {
      tenantId: this.selectedTenantId,
      accountId: account.accountId,
      currency: account.currencyCode || this.organisation?.baseCurrency || undefined,
      shortCode: this.organisation?.shortCode || undefined,
    });

    if (!response?.xeroPush) {
      // The API service has already surfaced the error details in a toast
      this.phase = { kind: 'ready' };
      return;
    }

    this.lastPush = response.xeroPush;
    await this.pollUntilSettled();
    if (this.destroyed) return;
    this.phase = { kind: 'result', push: this.lastPush };
  }

  /**
   * Xero delivers asynchronously. Polls are silent: a failed check just leaves
   * the status PENDING (the row re-checks on its next render), it never toasts.
   */
  private async pollUntilSettled() {
    for (let i = 0; i < MAX_POLLS && this.lastPush?.status === 'PENDING' && !this.destroyed; i++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (this.destroyed) return;
      const response = await this.bankFeed.refreshStatus(this.conversion.uid, true);
      if (response?.xeroPush) this.lastPush = response.xeroPush;
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
  }

  /** A REJECTED result can be retried with a different destination */
  tryAgain() {
    this.phase = { kind: 'ready' };
  }

  // View helpers

  get summary() {
    return this.preflight?.summary;
  }

  get checkingStatement(): boolean {
    return this.phase.kind === 'ready' && !this.preflight;
  }

  get selectedTenant(): XeroTenant | undefined {
    return this.tenants.find((t) => t.tenantId === this.selectedTenantId);
  }

  get selectedAccount(): XeroBankAccount | undefined {
    return this.accounts.find((a) => a.accountId === this.selectedAccountId);
  }

  get canSend(): boolean {
    return this.phase.kind === 'ready' && !!this.selectedAccount && !!this.preflight?.eligible;
  }

  get resultPush(): XeroPushState | null {
    return this.phase.kind === 'result' ? this.phase.push : null;
  }

  get reconcileUrl(): string | null {
    return xeroReconcileUrl(this.resultPush || undefined);
  }

  get rejectionDetail(): string {
    const error = this.resultPush?.errors?.[0];
    return error ? `${error.title || error.type || ''}${error.detail ? ' — ' + error.detail : ''}` : '';
  }

  get blockedPhase() {
    return this.phase.kind === 'blocked' ? this.phase : null;
  }

  connectXero() {
    this.dialogRef.close();
    this.xeroAuth.initiateXeroConnect().catch((e) => console.error(e));
  }

  close() {
    this.dialogRef.close(this.lastPush);
  }
}
